/**
 * QueueView Component
 *
 * Kanban-style view of investments organized by status.
 * Displays investments in columns representing their current workflow state.
 */

import { useInvestments } from '@medsuite/api-client';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import type { InvestmentWithRelations } from '@medsuite/models';

/**
 * Status column configuration
 */
interface StatusColumn {
  status: string;
  label: string;
  color: string;
}

/**
 * Status columns for the queue view
 * Deferred and Rejected are at the bottom
 */
const STATUS_COLUMNS: StatusColumn[] = [
  { status: 'new', label: 'New', color: 'bg-gray-50 dark:bg-gray-800' },
  { status: 'submitted', label: 'Submitted', color: 'bg-blue-50 dark:bg-blue-950' },
  { status: 'approved', label: 'Approved', color: 'bg-green-50 dark:bg-green-950' },
  { status: 'planned', label: 'Planned', color: 'bg-purple-50 dark:bg-purple-950' },
  { status: 'in_progress', label: 'In Progress', color: 'bg-indigo-50 dark:bg-indigo-950' },
  { status: 'complete', label: 'Complete', color: 'bg-emerald-50 dark:bg-emerald-950' },
  { status: 'deferred', label: 'Deferred', color: 'bg-yellow-50 dark:bg-yellow-950' },
  { status: 'rejected', label: 'Rejected', color: 'bg-red-50 dark:bg-red-950' },
];

/**
 * QueueView component props
 */
interface QueueViewProps {
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Warning Pill Component
 *
 * Small red pill for missing data indicators
 */
function WarningPill({ text }: { text: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-red-100 dark:bg-red-950 text-red-800 dark:text-red-200">
      <span className="rounded-full h-1 w-1 bg-red-400 dark:bg-red-500" aria-hidden="true" />
      {text}
    </span>
  );
}

/**
 * InvestmentCard Component
 *
 * Displays a single investment card in the queue
 */
function InvestmentCard({ investment }: { investment: InvestmentWithRelations }) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/investments/${investment.investment_id}`);
  };

  // Check for missing data (handle undefined or 0)
  const missingScore = !investment.composite_score || investment.composite_score === 0;
  const missingMilestones = !investment.milestone_count;
  const missingResources = !investment.resource_count;
  const missingMeasures = !investment.success_measure_count;
  const missingSponsors = !investment.sponsor_count;
  const missingContext =
    !investment.prime_objective ||
    !investment.problem_context ||
    !investment.improvement_ideas ||
    !investment.assumptions;

  const hasWarnings =
    missingScore ||
    missingMilestones ||
    missingResources ||
    missingMeasures ||
    missingSponsors ||
    missingContext;

  return (
    <div
      onClick={handleClick}
      className="cursor-pointer rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-sm transition-shadow hover:shadow-md"
    >
      {/* Investment ID */}
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
          #{String(investment.investment_id).padStart(4, '0')}
        </span>
        {investment.composite_score !== null && investment.composite_score > 0 && (
          <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900 px-2 py-0.5 text-xs font-medium text-blue-800 dark:text-blue-200">
            Score: {Math.round(investment.composite_score)}
          </span>
        )}
      </div>

      {/* Title */}
      <h4 className="mb-2 line-clamp-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        {investment.title}
      </h4>

      {/* PortCo */}
      {investment.portco_name && (
        <p className="mb-1 text-xs text-gray-600 dark:text-gray-400">{investment.portco_name}</p>
      )}

      {/* Submitted Date */}
      {investment.submitted_date && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {new Date(investment.submitted_date).toLocaleDateString()}
        </p>
      )}

      {/* Warning Pills */}
      {hasWarnings && (
        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-1">
          {missingScore && <WarningPill text="Missing Score" />}
          {missingMilestones && <WarningPill text="Missing Milestones" />}
          {missingResources && <WarningPill text="Missing Resources" />}
          {missingMeasures && <WarningPill text="Missing Measures" />}
          {missingSponsors && <WarningPill text="Missing Sponsors" />}
          {missingContext && <WarningPill text="Missing Context" />}
        </div>
      )}
    </div>
  );
}

/**
 * QueueView Component
 *
 * Features:
 * - Kanban-style board with status columns
 * - Investment cards grouped by status
 * - Count of investments per status
 * - Click card to navigate to investment detail
 * - Responsive horizontal scrolling
 * - Loading and error states
 * - Empty state per column
 */
export function QueueView({ className = '' }: QueueViewProps) {
  const { data: investments, isLoading, error } = useInvestments();

  /**
   * Group investments by status
   */
  const investmentsByStatus = useMemo(() => {
    if (!investments) {
      return {};
    }

    return investments.reduce(
      (acc, investment) => {
        const status = investment.status || 'new';
        if (!acc[status]) {
          acc[status] = [];
        }
        acc[status].push(investment);
        return acc;
      },
      {} as Record<string, InvestmentWithRelations[]>
    );
  }, [investments]);

  // Loading state
  if (isLoading) {
    return (
      <div className={`rounded-lg bg-white dark:bg-gray-800 p-8 shadow ${className}`}>
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 dark:border-t-blue-400"></div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading queue...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`rounded-lg bg-white dark:bg-gray-800 p-8 shadow ${className}`}>
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
            <svg
              className="h-6 w-6 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>
          <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
            Error loading queue
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg bg-white dark:bg-gray-800 shadow ${className}`}>
      <div className="p-4">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Approval Queue
        </h2>

        {/* Status Grid - Max 3 columns */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {STATUS_COLUMNS.map((column) => {
            const columnInvestments = investmentsByStatus[column.status] || [];
            const count = columnInvestments.length;

            return (
              <div
                key={column.status}
                className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-700"
              >
                {/* Column Header */}
                <div className={`rounded-t-lg px-4 py-3 ${column.color}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                      {column.label}
                    </h3>
                    <span className="inline-flex items-center rounded-full bg-white dark:bg-gray-800 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
                      {count}
                    </span>
                  </div>
                </div>

                {/* Column Content */}
                <div
                  className="flex-1 space-y-3 overflow-y-auto bg-gray-50 dark:bg-gray-900 p-3"
                  style={{ maxHeight: '600px' }}
                >
                  {count === 0 ? (
                    <div className="flex h-32 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800">
                      <p className="text-sm text-gray-500 dark:text-gray-400">No investments</p>
                    </div>
                  ) : (
                    columnInvestments.map((investment) => (
                      <InvestmentCard key={investment.investment_id} investment={investment} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary Stats */}
        {investments && investments.length > 0 && (
          <div className="mt-6 grid grid-cols-2 gap-4 border-t border-gray-200 dark:border-gray-700 pt-4 sm:grid-cols-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {investments.length}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Total Investments</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {(investmentsByStatus.submitted || []).length}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Awaiting Review</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {(investmentsByStatus.approved || []).length}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Approved</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {(investmentsByStatus.in_progress || []).length}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">In Progress</div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!investments || investments.length === 0) && (
          <div className="py-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
              No investments in queue
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Get started by creating a new technology investment request
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default QueueView;
